import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Tag, Gift, Check, ShoppingBag, Upload, Home, Plus, Star, Trash2, Truck, Bike, Store } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/use-page-title";
import { useCart, shopClient } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const BLANK_ADDR = {
  label: "Home",
  full_name: "",
  phone: "",
  email: "",
  line1: "",
  line2: "",
  city: "",
  state: "Maharashtra",
  pincode: "",
  country: "India",
  landmark: "",
  company_name: "",
  gstin: "",
};

// GSTIN format: 15 chars — 2 digit state code, 10 char PAN, 1 digit entity, 1 alpha 'Z', 1 alnum checksum
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  usePageTitle("Checkout");
  const nav = useNavigate();
  const { user } = useAuth();
  const { cart, validateCoupon, validateGiftCard, loading } = useCart();
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(false);

  const [address, setAddress] = useState({ ...BLANK_ADDR });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [saveNewAddress, setSaveNewAddress] = useState(true);

  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [gcCode, setGcCode] = useState("");
  const [gc, setGc] = useState(null);
  const [gcErr, setGcErr] = useState("");
  const [notes, setNotes] = useState("");
  const [waOptin, setWaOptin] = useState(true);
  const [designLater, setDesignLater] = useState(false);
  const [wantGst, setWantGst] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("courier"); // pickup | local_agent | courier
  const [shipCfg, setShipCfg] = useState(null); // { courier_fee, free_shipping_threshold, local_agent_cities, enable_* }
  const gstinValid = !address.gstin || GSTIN_RE.test((address.gstin || "").toUpperCase());

  // Load public shipping config
  useEffect(() => {
    shopClient.get("/shipping/config").then((r) => setShipCfg(r.data)).catch(() => {});
  }, []);

  // MANDATORY login gate
  useEffect(() => {
    if (user === false) nav(`/login?next=${encodeURIComponent("/checkout")}`);
  }, [user, nav]);

  // Bounce back if cart empty (but not while placing/placed)
  useEffect(() => {
    if (placing || placed) return;
    if (!loading && cart.items.length === 0) nav("/cart");
  }, [cart.items.length, loading, nav, placing, placed]);

  // Fetch saved addresses on mount
  useEffect(() => {
    if (!user) return;
    shopClient.get("/me/addresses").then((r) => {
      const rows = r.data || [];
      setSavedAddresses(rows);
      if (rows.length > 0) {
        const def = rows.find((a) => a.is_default) || rows[0];
        setSelectedAddrId(def.id);
        setAddress({ ...BLANK_ADDR, ...def });
        setShowNewForm(false);
      } else {
        setShowNewForm(true);
        setAddress((a) => ({
          ...a,
          full_name: user?.name || "",
          phone: user?.phone || "",
          email: user?.email || "",
        }));
      }
    }).catch(() => {});
  }, [user]);

  const pickSaved = (a) => {
    setSelectedAddrId(a.id);
    setAddress({ ...BLANK_ADDR, ...a });
    setShowNewForm(false);
    setSaveNewAddress(false);
    if ((a.company_name || "").trim() || (a.gstin || "").trim()) setWantGst(true);
  };

  const openNewForm = () => {
    setSelectedAddrId("");
    setShowNewForm(true);
    setSaveNewAddress(true);
    setAddress({
      ...BLANK_ADDR,
      full_name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
    });
  };

  const removeSaved = async (a) => {
    if (!window.confirm(`Delete "${a.label}" address?`)) return;
    try {
      await shopClient.delete(`/me/addresses/${a.id}`);
      const rows = (await shopClient.get("/me/addresses")).data;
      setSavedAddresses(rows);
      if (selectedAddrId === a.id) {
        if (rows.length > 0) pickSaved(rows.find((x) => x.is_default) || rows[0]);
        else openNewForm();
      }
      toast.success("Address deleted");
    } catch { toast.error("Could not delete"); }
  };

  const subtotal = cart.subtotal;
  const isLocalCity = (() => {
    if (!shipCfg) return false;
    const cities = (shipCfg.local_agent_cities || []).map((c) => (c || "").trim().toLowerCase());
    return cities.includes((address.city || "").trim().toLowerCase());
  })();
  // If admin disabled a method or the user's city isn't eligible for local delivery, snap back to courier
  useEffect(() => {
    if (!shipCfg) return;
    if (shippingMethod === "local_agent" && (!shipCfg.enable_local_agent || !isLocalCity)) {
      setShippingMethod(shipCfg.enable_courier ? "courier" : shipCfg.enable_pickup ? "pickup" : "courier");
    }
    if (shippingMethod === "pickup" && !shipCfg.enable_pickup) {
      setShippingMethod(shipCfg.enable_courier ? "courier" : "local_agent");
    }
    if (shippingMethod === "courier" && !shipCfg.enable_courier) {
      setShippingMethod(shipCfg.enable_pickup ? "pickup" : "local_agent");
    }
  }, [shipCfg, isLocalCity, shippingMethod]);
  const courierFee = shipCfg?.courier_fee ?? 60;
  const freeThreshold = shipCfg?.free_shipping_threshold ?? 1000;
  const courierShipping = subtotal === 0 ? 0 : (subtotal >= freeThreshold ? 0 : courierFee);
  const shipping = shippingMethod === "courier" ? courierShipping : 0;
  const couponDiscount = coupon?.discount || 0;
  const afterCoupon = Math.max(0, subtotal - couponDiscount);
  const gcApplied = gc ? Math.min(gc.balance, afterCoupon) : 0;
  const total = Math.max(0, subtotal - couponDiscount - gcApplied + shipping);

  const applyCoupon = async () => {
    setCouponErr(""); setCoupon(null);
    if (!couponCode.trim()) return;
    try {
      const { discount } = await validateCoupon(couponCode.trim().toUpperCase(), subtotal);
      setCoupon({ code: couponCode.trim().toUpperCase(), discount });
      toast.success(`Coupon applied — ₹${discount} off`);
    } catch (e) { setCouponErr(e?.response?.data?.detail || "Invalid coupon"); }
  };
  const applyGc = async () => {
    setGcErr(""); setGc(null);
    if (!gcCode.trim()) return;
    try {
      const g = await validateGiftCard(gcCode.trim().toUpperCase());
      setGc({ code: gcCode.trim().toUpperCase(), balance: g.balance });
      toast.success(`Gift card applied — ₹${g.balance} available`);
    } catch (e) { setGcErr(e?.response?.data?.detail || "Invalid gift card"); }
  };

  const requiredFields = ["full_name", "phone", "line1", "city", "state", "pincode"];
  const canPay = requiredFields.every((f) => (address[f] || "").trim().length > 0) && gstinValid;

  const payAndPlace = async () => {
    if (!canPay) {
      if (!gstinValid) { toast.error("Invalid GSTIN — please check the 15-character format"); return; }
      toast.error("Please fill in all required address fields"); return;
    }
    setPlacing(true);
    const scriptOk = await loadRazorpayScript();
    if (!scriptOk) {
      setPlacing(false);
      toast.error("Could not load Razorpay. Check your internet & try again.");
      return;
    }
    try {
      // 1) Get Razorpay key
      const { data: cfg } = await shopClient.get(`/payments/razorpay/config`);
      if (!cfg.configured) {
        setPlacing(false);
        toast.error("Payment gateway is temporarily offline. Please try again shortly.");
        return;
      }
      // 2) Initialise payment — creates ONLY a payment_intent + Razorpay order
      const finalAddr = { ...address };
      if (!wantGst) { finalAddr.company_name = ""; finalAddr.gstin = ""; }
      else { finalAddr.gstin = (finalAddr.gstin || "").toUpperCase().trim(); finalAddr.company_name = (finalAddr.company_name || "").trim(); }
      const initPayload = {
        address: finalAddr,
        coupon_code: coupon?.code || "",
        gift_card_code: gc?.code || "",
        notes,
        design_upload_later: designLater,
        save_address: showNewForm && saveNewAddress,
        shipping_method: shippingMethod,
        whatsapp_optin: waOptin,
      };
      const { data: init } = await shopClient.post("/shop/pay/init", initPayload);

      // 3) Open Razorpay modal
      const options = {
        key: cfg.key_id,
        amount: init.amount,
        currency: init.currency,
        order_id: init.razorpay_order_id,
        name: "Brothers Advertising",
        description: `Order payment · ₹${(init.amount / 100).toFixed(2)}`,
        prefill: init.customer,
        theme: { color: "#FF6B00" },
        handler: async (resp) => {
          // 4) Verify signature & materialize shop_order
          try {
            const { data: done } = await shopClient.post("/shop/pay/complete", {
              intent_id: init.intent_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setPlaced(true);
            toast.success("Payment successful — order placed!");
            nav(`/order-confirmation/${done.order_id}`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment succeeded but confirmation failed. Please contact support.");
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPlacing(false);
            toast("Payment cancelled — no order was placed.", { icon: "⚠️" });
          },
        },
      };
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setPlacing(false);
        toast.error(resp?.error?.description || "Payment failed — please try again.");
      });
      rzp.open();
    } catch (e) {
      setPlacing(false);
      toast.error(e?.response?.data?.detail || "Could not initiate payment");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-[#FF6B00]" data-testid="back-to-cart">
        <ArrowLeft size={14} /> Back to Cart
      </Link>

      <div className="mt-4 mb-8">
        <div className="overline text-[#FF6B00]">Checkout · Step 2 of 3</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter">Checkout</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Saved Addresses */}
          {savedAddresses.length > 0 && (
            <section className="border-2 border-zinc-200 rounded-2xl bg-white p-6" data-testid="saved-addresses">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-2xl font-black tracking-tighter">Ship to</div>
                <button onClick={openNewForm} className="text-xs font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1" data-testid="add-new-address-btn">
                  <Plus size={12} /> Use new address
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {savedAddresses.map((a) => {
                  const sel = selectedAddrId === a.id && !showNewForm;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => pickSaved(a)}
                      className={`text-left p-4 rounded-xl border-2 transition-all relative ${sel ? "border-[#FF6B00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`saved-addr-${a.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Home size={14} className="text-[#FF6B00]" />
                        <span className="font-bold text-sm">{a.label || "Address"}</span>
                        {a.is_default && (
                          <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider inline-flex items-center gap-0.5">
                            <Star size={9} /> Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-zinc-700">{a.full_name} · {a.phone}</div>
                      <div className="text-xs text-zinc-500 mt-1 leading-relaxed">
                        {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                        {a.city}, {a.state} — {a.pincode}
                      </div>
                      <span
                        onClick={(e) => { e.stopPropagation(); removeSaved(a); }}
                        className="absolute top-3 right-3 text-zinc-400 hover:text-red-500 cursor-pointer"
                        data-testid={`addr-del-${a.id}`}
                      >
                        <Trash2 size={14} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* New / Edit Address form */}
          {(showNewForm || savedAddresses.length === 0) && (
            <section className="border-2 border-zinc-200 rounded-2xl bg-white p-6" data-testid="address-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-2xl font-black tracking-tighter">
                  {savedAddresses.length > 0 ? "New Shipping Address" : "Shipping Address"}
                </div>
                {savedAddresses.length > 0 && (
                  <button onClick={() => { setShowNewForm(false); const def = savedAddresses.find((a) => a.is_default) || savedAddresses[0]; if (def) pickSaved(def); }} className="text-xs text-zinc-500 hover:text-[#FF6B00]" data-testid="cancel-new-addr">
                    Cancel
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Address Label" value={address.label} onChange={(v) => setAddress({ ...address, label: v })} testid="addr-label" placeholder="Home, Office, Warehouse…" />
                <div /> {/* spacer */}
                <Input label="Full Name*" value={address.full_name} onChange={(v) => setAddress({ ...address, full_name: v })} testid="addr-name" />
                <Input label="Phone*" value={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} testid="addr-phone" />
                <Input label="Email" value={address.email} onChange={(v) => setAddress({ ...address, email: v })} testid="addr-email" className="md:col-span-2" />
                <Input label="Address Line 1*" value={address.line1} onChange={(v) => setAddress({ ...address, line1: v })} testid="addr-line1" className="md:col-span-2" />
                <Input label="Address Line 2" value={address.line2} onChange={(v) => setAddress({ ...address, line2: v })} testid="addr-line2" className="md:col-span-2" />
                <Input label="City*" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} testid="addr-city" />
                <Input label="State*" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} testid="addr-state" />
                <Input label="PIN Code*" value={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} testid="addr-pincode" />
                <Input label="Landmark" value={address.landmark || ""} onChange={(v) => setAddress({ ...address, landmark: v })} testid="addr-landmark" />
              </div>

              {showNewForm && (
                <label className="mt-4 flex items-center gap-2 text-sm text-zinc-600 cursor-pointer" data-testid="save-addr-toggle-wrap">
                  <input
                    type="checkbox"
                    checked={saveNewAddress}
                    onChange={(e) => setSaveNewAddress(e.target.checked)}
                    data-testid="save-addr-toggle"
                  />
                  Save this address to my address book for next time
                </label>
              )}
            </section>
          )}

          {/* GST Invoice / Business Billing */}
          <section className="border-2 border-zinc-200 rounded-2xl bg-white p-6" data-testid="gst-section">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="want-gst-toggle-wrap">
              <input
                type="checkbox"
                checked={wantGst}
                onChange={(e) => setWantGst(e.target.checked)}
                className="mt-1"
                data-testid="want-gst-toggle"
              />
              <div className="flex-1">
                <div className="font-display text-lg font-black tracking-tight">Bill to my company (GST invoice)</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Tick this if you'd like your <b>Company Name</b> &amp; <b>GSTIN</b> printed on the tax invoice so you can claim Input Tax Credit.
                </div>
              </div>
            </label>

            {wantGst && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="gst-fields-wrap">
                <Input
                  label="Company Name"
                  value={address.company_name || ""}
                  onChange={(v) => setAddress({ ...address, company_name: v })}
                  testid="checkout-company-name"
                  placeholder="e.g. Acme Print Solutions Pvt. Ltd."
                  className="md:col-span-2"
                />
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">GSTIN</label>
                  <input
                    value={address.gstin || ""}
                    onChange={(e) => setAddress({ ...address, gstin: e.target.value.toUpperCase() })}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    className={`w-full border-2 rounded-xl px-4 py-2 text-sm bg-white outline-none font-mono tracking-wider ${
                      !address.gstin ? "border-zinc-200 focus:border-[#FF6B00]" :
                      gstinValid ? "border-emerald-400 focus:border-emerald-500" :
                      "border-red-400 focus:border-red-500"
                    }`}
                    data-testid="checkout-gstin"
                  />
                  <div className="mt-1 text-xs">
                    {!address.gstin ? (
                      <span className="text-zinc-500">15-character GSTIN. Leave blank if unregistered.</span>
                    ) : gstinValid ? (
                      <span className="text-emerald-700 font-bold">✓ Valid GSTIN format</span>
                    ) : (
                      <span className="text-red-600 font-bold">✗ Invalid format — must be 15 characters (e.g. 27ABCDE1234F1Z5)</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Order notes */}
          <section className="border-2 border-zinc-200 rounded-2xl bg-white p-6">
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Order notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-4 py-2 text-sm bg-white outline-none"
              placeholder="Any specific instructions for our team?"
              data-testid="order-notes"
            />
          </section>

          {/* WhatsApp opt-in */}
          <section className="border-2 border-emerald-200 bg-emerald-50/60 rounded-2xl p-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="whatsapp-optin-wrap">
              <input
                type="checkbox"
                checked={waOptin}
                onChange={(e) => setWaOptin(e.target.checked)}
                className="mt-1 accent-emerald-600"
                data-testid="whatsapp-optin-checkbox"
              />
              <div className="flex-1">
                <div className="font-bold text-sm text-emerald-900">Get order updates &amp; invoice on WhatsApp</div>
                <div className="text-xs text-emerald-800/80 mt-0.5">Order confirmation, live status updates and your GST invoice PDF — sent to <span className="font-mono">{address.phone || "your mobile"}</span>. You can opt out anytime.</div>
              </div>
            </label>
          </section>

          {/* Design upload later — legacy fallback, hide if every cart line already has a per-line design_choice */}
          {(cart.items || []).some((i) => !(i.design_choice || "").trim()) && (
          <section className="border-2 border-zinc-200 rounded-2xl bg-white p-6">
            <div className="font-display text-2xl font-black tracking-tighter mb-3 inline-flex items-center gap-2">
              <Upload size={22} className="text-[#FF6B00]" /> Artwork / Design File
            </div>
            <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-zinc-200 hover:border-[#FF6B00] cursor-pointer transition-colors" data-testid="design-later-toggle-wrap">
              <input type="checkbox" checked={designLater} onChange={(e) => setDesignLater(e.target.checked)} className="mt-1" data-testid="design-later-toggle" />
              <div className="flex-1">
                <div className="font-bold text-sm">Buy Now, upload artwork later</div>
                <div className="text-xs text-zinc-500 mt-0.5">Pay for the order now and upload your design file within 7 days from the confirmation page. Ideal if your file isn't print-ready yet.</div>
              </div>
            </label>
            {!designLater && (
              <div className="mt-2 text-xs text-zinc-500 pl-4">💡 Prefer to upload now? Just leave this unchecked — you'll get an upload widget on the confirmation page after paying.</div>
            )}
          </section>
          )}

          {/* Summary of per-line design choices (informational) */}
          {(cart.items || []).some((i) => (i.design_choice || "").trim()) && (
            <section className="border-2 border-emerald-200 bg-emerald-50 rounded-2xl p-4" data-testid="design-summary-block">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-800 mb-2">Design Instructions Received</div>
              <ul className="text-sm text-emerald-900 space-y-1">
                {(cart.items || []).filter((i) => (i.design_choice || "").trim()).map((i) => (
                  <li key={i.line_id} className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 text-emerald-600 flex-shrink-0" />
                    <span><b>{i.name}</b> — {
                      i.design_choice === "upload_now" ? `${(i.design_files || []).length} file uploaded ✓` :
                      i.design_choice === "upload_later" ? "will upload after payment" :
                      i.design_choice === "design_for_me" ? "our team will design" : ""
                    }</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Summary + Pay */}
        <aside className="lg:col-span-1">
          <div className="border-2 border-zinc-200 rounded-2xl bg-white p-6 sticky top-24">
            <div className="font-display text-2xl font-black tracking-tighter mb-4">Order Summary</div>

            {/* Shipping method */}
            {shipCfg && (
              <div className="mb-4" data-testid="shipping-methods">
                <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2 inline-flex items-center gap-1"><Truck size={11} /> Shipping Method</label>
                <div className="space-y-2">
                  {shipCfg.enable_pickup && (
                    <ShipOption
                      icon={Store}
                      testid="ship-pickup"
                      selected={shippingMethod === "pickup"}
                      onSelect={() => setShippingMethod("pickup")}
                      title="Pickup from Shop"
                      desc="Collect from our Navi Mumbai shop — no delivery fee."
                      fee="FREE"
                    />
                  )}
                  {shipCfg.enable_local_agent && (
                    <ShipOption
                      icon={Bike}
                      testid="ship-local-agent"
                      selected={shippingMethod === "local_agent"}
                      onSelect={() => isLocalCity && setShippingMethod("local_agent")}
                      disabled={!isLocalCity}
                      title="Delivery by Rapido / Porter"
                      desc={isLocalCity
                        ? "Fastest for local orders — pay the delivery agent directly on drop-off."
                        : `Local delivery only for ${(shipCfg.local_agent_cities || []).join(", ")}. Enter a matching city above.`}
                      fee="Pay Agent"
                    />
                  )}
                  {shipCfg.enable_courier && (
                    <ShipOption
                      icon={Truck}
                      testid="ship-courier"
                      selected={shippingMethod === "courier"}
                      onSelect={() => setShippingMethod("courier")}
                      title="Delivery by Courier"
                      desc={courierShipping === 0 && subtotal > 0
                        ? `Free courier — orders above ₹${Math.round(freeThreshold).toLocaleString("en-IN")}.`
                        : `Standard courier delivery — ₹${Math.round(courierFee)}. Free above ₹${Math.round(freeThreshold).toLocaleString("en-IN")}.`}
                      fee={courierShipping === 0 ? "FREE" : `₹${Math.round(courierFee)}`}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Coupon */}
            <div className="mb-3">
              <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1 inline-flex items-center gap-1"><Tag size={11} /> Coupon</label>
              <div className="flex gap-2">
                <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm bg-white outline-none uppercase" data-testid="coupon-input" />
                <button onClick={applyCoupon} className="btn-swiss btn-outline text-xs" data-testid="coupon-apply">Apply</button>
              </div>
              {couponErr && <div className="text-xs text-red-500 mt-1">{couponErr}</div>}
              {coupon && <div className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1"><Check size={12} /> {coupon.code} · ₹{coupon.discount} off</div>}
            </div>

            {/* Gift Card */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1 inline-flex items-center gap-1"><Gift size={11} /> Gift Card</label>
              <div className="flex gap-2">
                <input value={gcCode} onChange={(e) => setGcCode(e.target.value.toUpperCase())} placeholder="GC-XXXX-XXXX" className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm bg-white outline-none uppercase" data-testid="gc-input" />
                <button onClick={applyGc} className="btn-swiss btn-outline text-xs" data-testid="gc-apply">Apply</button>
              </div>
              {gcErr && <div className="text-xs text-red-500 mt-1">{gcErr}</div>}
              {gc && <div className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1"><Check size={12} /> {gc.code} · ₹{gcApplied} redeemed of ₹{gc.balance}</div>}
            </div>

            {/* Totals */}
            <div className="border-t border-zinc-200 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-zinc-600"><span>Subtotal ({cart.count} items)</span><span className="font-mono">₹{Math.round(subtotal).toLocaleString("en-IN")}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-emerald-700"><span>Coupon ({coupon.code})</span><span className="font-mono">−₹{Math.round(couponDiscount).toLocaleString("en-IN")}</span></div>}
              {gcApplied > 0 && <div className="flex justify-between text-emerald-700"><span>Gift Card ({gc.code})</span><span className="font-mono">−₹{Math.round(gcApplied).toLocaleString("en-IN")}</span></div>}
              <div className="flex justify-between text-zinc-600" data-testid="summary-shipping">
                <span>
                  Shipping · <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                    {shippingMethod === "pickup" ? "Pickup" : shippingMethod === "local_agent" ? "Local Agent" : "Courier"}
                  </span>
                </span>
                <span className="font-mono">
                  {shippingMethod === "local_agent" ? "Paid to agent" : (shipping === 0 ? "FREE" : `₹${shipping}`)}
                </span>
              </div>
              <div className="border-t border-zinc-200 pt-2 flex justify-between text-base font-black">
                <span>Total</span>
                <span className="font-mono text-[#FF6B00]" data-testid="checkout-total">₹{Math.round(total).toLocaleString("en-IN")}</span>
              </div>
              <div className="text-[10px] text-emerald-700 font-bold text-right">Incl. of GST</div>
            </div>

            <button
              onClick={payAndPlace}
              disabled={placing || !canPay || total <= 0}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full hover:bg-[#e65a00] disabled:opacity-50"
              data-testid="pay-place-order-btn"
            >
              <ShoppingBag size={16} /> {placing ? "Opening Razorpay…" : `Pay Securely · ₹${Math.round(total).toLocaleString("en-IN")}`}
            </button>
            <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-zinc-500 text-center">
              <span className="text-emerald-600">🔒</span> Secured by Razorpay · UPI · Cards · Netbanking · Wallets
            </div>
            <div className="mt-1 text-[10px] text-zinc-500 text-center">Order is created only after payment succeeds. By paying you agree to our <Link to="/terms" className="underline hover:text-[#FF6B00]">Terms</Link>.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, className = "", placeholder = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-4 py-2 text-sm bg-white outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function ShipOption({ icon: Icon, title, desc, fee, selected, onSelect, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
        disabled
          ? "border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed"
          : selected
          ? "border-[#FF6B00] bg-orange-50"
          : "border-zinc-200 bg-white hover:border-zinc-400"
      }`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${selected ? "bg-[#FF6B00] text-white" : "bg-zinc-100 text-zinc-500"}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-bold text-zinc-900">{title}</div>
            <div className={`text-[11px] font-mono font-bold ${selected ? "text-[#FF6B00]" : "text-zinc-600"}`}>{fee}</div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{desc}</div>
        </div>
      </div>
    </button>
  );
}
